import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import TranscriptView from '../components/TranscriptView';
import type { TranscriptEntry } from '@mia/shared';

interface Conversation {
  id: string;
  channel: 'web' | 'phone';
  status: string;
  contactId: string | null;
  contactPhone: string | null;
  roomName: string | null;
  startedAt: string | null;
  endedAt: string | null;
  lastError: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface ConversationEvent {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  timestamp: string;
}

interface Artifact {
  id: string;
  type: string;
  url: string;
  createdAt: string;
}

export default function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [events, setEvents] = useState<ConversationEvent[]>([]);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadConversation();
    }
  }, [id]);

  const loadConversation = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [convResponse, eventsResponse, artifactsResponse] = await Promise.all([
        api.get<Conversation>(`/conversations/${id}`),
        api.get<{ events: ConversationEvent[] }>(`/conversations/${id}/events`),
        api.get<{ artifacts: Artifact[] }>(`/conversations/${id}/artifacts`),
      ]);

      setConversation(convResponse.data || null);
      setEvents(eventsResponse.data?.events || []);
      setArtifacts(artifactsResponse.data?.artifacts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  };

  // Convert events to transcript entries for display
  const transcriptEntries: TranscriptEntry[] = events
    .filter((e) => e.type === 'transcript_update')
    .map((e) => ({
      id: e.id,
      role: (e.payload.role as 'user' | 'agent' | 'system') || 'system',
      content: (e.payload.content as string) || '',
      timestamp: new Date(e.timestamp).getTime(),
      isFinal: true,
    }));

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-surface-400">Loading...</div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="p-8">
        <div className="card p-6 text-center">
          <p className="text-red-400 mb-4">{error || 'Conversation not found'}</p>
          <Link to="/conversations" className="btn-secondary">
            Back to Conversations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/conversations" className="text-surface-400 hover:text-surface-200">
          ← Back
        </Link>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-white">
            Conversation Details
          </h1>
          <p className="font-mono text-sm text-surface-400">{conversation.id}</p>
        </div>
        <StatusBadge status={conversation.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Details */}
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-display text-lg font-semibold text-white">Information</h2>
            </div>
            <div className="card-body space-y-3">
              <InfoRow label="Channel" value={conversation.channel} />
              <InfoRow label="Status" value={conversation.status} />
              <InfoRow label="Room Name" value={conversation.roomName || '-'} mono />
              <InfoRow
                label="Started"
                value={
                  conversation.startedAt
                    ? new Date(conversation.startedAt).toLocaleString()
                    : '-'
                }
              />
              <InfoRow
                label="Ended"
                value={
                  conversation.endedAt
                    ? new Date(conversation.endedAt).toLocaleString()
                    : '-'
                }
              />
              {conversation.startedAt && conversation.endedAt && (
                <InfoRow
                  label="Duration"
                  value={formatDuration(
                    new Date(conversation.endedAt).getTime() -
                      new Date(conversation.startedAt).getTime()
                  )}
                />
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-display text-lg font-semibold text-white">Contact</h2>
            </div>
            <div className="card-body space-y-3">
              <InfoRow label="Contact ID" value={conversation.contactId || '-'} mono />
              <InfoRow label="Phone" value={conversation.contactPhone || '-'} />
            </div>
          </div>

          {/* Error */}
          {conversation.lastError && (
            <div className="card border-red-500/30">
              <div className="card-header">
                <h2 className="font-display text-lg font-semibold text-red-400">Error</h2>
              </div>
              <div className="card-body">
                <p className="text-sm text-red-300">{conversation.lastError}</p>
              </div>
            </div>
          )}

          {/* Artifacts */}
          {artifacts.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="font-display text-lg font-semibold text-white">Artifacts</h2>
              </div>
              <div className="card-body space-y-2">
                {artifacts.map((artifact) => (
                  <div
                    key={artifact.id}
                    className="flex items-center justify-between p-2 rounded bg-surface-800"
                  >
                    <div>
                      <span className="badge badge-info mr-2">{artifact.type}</span>
                      <span className="text-xs text-surface-500">
                        {new Date(artifact.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <a
                      href={artifact.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:text-primary-300 text-sm"
                    >
                      View →
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Middle column - Transcript */}
        <div className="card h-[600px] flex flex-col">
          <div className="card-header">
            <h2 className="font-display text-lg font-semibold text-white">Transcript</h2>
          </div>
          <div className="flex-1 min-h-0">
            {transcriptEntries.length > 0 ? (
              <TranscriptView entries={transcriptEntries} />
            ) : (
              <div className="h-full flex items-center justify-center text-surface-500">
                No transcript available
              </div>
            )}
          </div>
        </div>

        {/* Right column - Events */}
        <div className="card h-[600px] flex flex-col">
          <div className="card-header">
            <h2 className="font-display text-lg font-semibold text-white">
              Events ({events.length})
            </h2>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {events.length > 0 ? (
              <div className="p-4 space-y-3">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="p-3 rounded-lg bg-surface-800 border border-surface-700"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="badge badge-info">{event.type}</span>
                      <span className="text-xs text-surface-500">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    {Object.keys(event.payload).length > 0 && (
                      <pre className="text-xs text-surface-400 overflow-x-auto">
                        {JSON.stringify(event.payload, null, 2)}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-surface-500">
                No events recorded
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-surface-500">{label}</span>
      <span className={`text-surface-200 ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusClasses: Record<string, string> = {
    active: 'badge-success',
    completed: 'badge-info',
    pending: 'badge-warning',
    connecting: 'badge-warning',
    failed: 'badge-error',
    cancelled: 'badge-error',
  };

  return <span className={`badge ${statusClasses[status] || 'badge-info'}`}>{status}</span>;
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}




